import get from 'lodash/get'
import { event as d3event, mouse as d3mouse } from 'd3-selection'
import { zoom, zoomIdentity } from 'd3-zoom'
import findMapObjectByCoordinates from './findMapObjectByCoordinates'
import findBlockInstanceByCoordinates from './findBlockInstanceByCoordinates'
import ZoomWidget from './ZoomWidget'

const translateExtent = (map) => [
  [-map.screen.w / 2, -map.screen.h / 2],
  [map.screen.w * 1.5, map.screen.h * 1.5],
]

class Zoom {
  constructor(map) {
    this.map = map

    this.zoom = zoom()
      .scaleExtent([map.minScale, map.maxScale])
      .translateExtent(translateExtent(map))
      .on('start', () => {
        this.startEventTimestamp = Date.now()
      })
      .on('zoom', map.onZoom)
      .on('end', () => {
        const endEventTimestamp = Date.now()
        if (endEventTimestamp - this.startEventTimestamp > 100) {
          map.onZoomEnd()
        }
      })
      .filter(() => this.filter())

    map.container
      .call(this.zoom.transform, () => zoomIdentity.translate(0, 0).scale(1))
      .on('zoom', null)
      .call(this.zoom)

    this.zoomWidget = new ZoomWidget({ ...map, ...this })
  }

  update = (map) => {
    this.map = map
    this.zoom.scaleExtent([map.minScale, map.maxScale]).translateExtent(translateExtent(map))
    this.zoomWidget.udpate({ ...map, zoom: this.zoom })
  }

  // Zoom filter
  filter = () => {
    if (['floorImage', 'floorImageContainer'].indexOf(d3event.srcElement.id) < 0) {
      return false
    }

    const { map } = this
    // Ignore zoom on right click
    if (d3event.button === 2) {
      return false
    }

    if (this.map.finder) {
      map.tooltip.hideTooltip()
      return true
    }

    // Handle as zoom on wheel event
    if (d3event.type === 'wheel') {
      return true
    }

    map.tooltip && map.tooltip.hideTooltip()

    const mouse = d3mouse(map.container.node())

    if (map.handles) {
      const handle = map.handles.reduce((result, h) => {
        if (
          mouse[0] >= h.cx - h.r &&
          mouse[0] <= h.cx + h.r &&
          mouse[1] >= h.cy - h.r &&
          mouse[1] <= h.cy + h.r
        ) {
          return h
        }

        return result
      }, null)

      if (handle) {
        return false
      }
    }

    const mapObject = map.editingBlocks
      ? findBlockInstanceByCoordinates(mouse, map)
      : findMapObjectByCoordinates(mouse, map)

    if (mapObject) {
      // Pass to mapObject drag handler if edtimap and mapObject selected
      if (map.enableMapObjectDrag) {
        return !map.selectedIds[mapObject.id]
      }

      // Handle as zoom if space is empty
      if (
        !get(mapObject, 'spaceAssignment.employee') ||
        (!map.enableEmployeeDrag && get(mapObject, 'spaceAssignment.employee'))
      ) {
        return true
      }
    } else {
      // Handle as zoom if no space
      return true
    }

    // Pass to drag handler if admin or department admin and has manage rights
    return false
  }

  zoomBy = (k, animate = false) => {
    const { map } = this

    if (k < map.minScale) {
      k = map.minScale
    } else if (k > map.maxScale) {
      k = map.maxScale
    }
    this.zoom.scaleTo(map.container.transition().duration(animate ? 350 : 0), k)
  }

  zoomIn = () => {
    const { map } = this

    this.zoom.scaleTo(
      map.container.transition().duration(350),
      2 * map.transform.k > map.maxScale ? map.maxScale : 2 * map.transform.k
    )
  }

  zoomOut = () => {
    const { map } = this

    this.zoom.scaleTo(
      map.container.transition().duration(350),
      0.5 * map.transform.k < map.minScale ? map.minScale : 0.5 * map.transform.k
    )
  }

  zoomTo = ({ x, y, k = 4, animate }) => {
    const { container, imageScale, screen, dx, dy } = this.map

    container
      .transition()
      .duration(animate ? 350 : 0)
      .call(this.zoom.transform, () => {
        return zoomIdentity
          .translate(screen.w / 2, screen.h / 2)
          .scale(k)
          .translate(-imageScale(x) - dx, -imageScale(y) - dy)
      })
  }

  resetZoom = () =>
    this.map.container
      .transition()
      .duration(350)
      .call(this.zoom.transform, () => zoomIdentity.translate(0, 0).scale(1))
}

export default Zoom
